import React from 'react';
import { ArrowRightLeft, Flag, Warehouse } from 'lucide-react';

import { Badge } from 'shadcn/components/ui/Badge';

/**
 * Cross-dock glyph with overlaid arrows, used to indicate cross-dock purchase orders.
 *
 * @returns {JSX.Element} Cross-dock icon.
 */
export function CrossDockIconBadge() {
    return (
        <span className="relative inline-flex h-4 w-4 items-center justify-center text-current" aria-hidden="true">
            <Warehouse className="h-4 w-4" aria-hidden="true" />
            <span className="absolute -bottom-1 -right-1 inline-flex h-3 w-3 items-center justify-center rounded-sm bg-orange-100 border border-orange-200 text-orange-700 shadow-sm">
                <ArrowRightLeft className="h-2.5 w-2.5" aria-hidden="true" />
            </span>
        </span>
    );
}

/**
 * Renders the status cell content for a purchase order row.
 *
 * @param {Object} props - Component props.
 * @param {string | null | undefined} props.status - Raw status value from the table row.
 * @param {boolean} [props.isCrossDock=false] - Indicates whether the row represents a cross-dock purchase order.
 * @param {boolean} [props.isPriority=false] - Indicates whether the row should display the priority indicator.
 * @param {string} [props.fallbackText='—'] - Fallback text when no status is available.
 * @returns {JSX.Element | null} Status cell JSX.
 */
export function PurchaseOrderStatusCell({ fallbackText = '—', isCrossDock = false, isPriority = false, status }) {
    const statusValue = String(status || fallbackText);
    const normalizedStatus = statusValue.toUpperCase();

    const statusBadgeClassName = (() => {
        if (normalizedStatus === 'CONSUMED') {
            return 'bg-sky-600 text-white border-sky-700 hover:bg-sky-600 hover:text-white';
        }
        if (normalizedStatus === 'CANCELLED') {
            return 'bg-slate-200 text-slate-800 border-slate-300 hover:bg-slate-200 hover:text-slate-800';
        }
        return 'bg-sky-100 text-sky-900 border-sky-200 hover:bg-sky-100 hover:text-sky-900';
    })();

    const chips = [
        {
            key: 'status',
            type: 'badge',
            content: statusValue,
            variant: 'outline',
            className: statusBadgeClassName,
        },
    ];

    if (isCrossDock) {
        chips.push({
            key: 'cross-dock',
            type: 'badge',
            content: <CrossDockIconBadge />,
            variant: 'outline',
            className:
                'bg-orange-100 text-orange-900 border-orange-200 hover:bg-orange-100 hover:text-orange-900 flex items-center gap-1',
            ariaLabel: 'Cross dock purchase order',
        });
    }

    if (isPriority) {
        chips.push({
            key: 'priority',
            type: 'icon',
            content: <Flag className="h-4 w-4 text-red-600" aria-hidden="true" />,
            className: 'inline-flex items-center text-red-600',
            ariaLabel: 'Priority purchase order',
        });
    }

    if (!chips.length) return null;

    return (
        <div className="flex flex-wrap items-center gap-2">
            {chips.map(({ ariaLabel, className = '', content, key, type, variant }) =>
                type === 'icon' ? (
                    <span key={key} className={className} aria-label={ariaLabel} role="img">
                        {content}
                    </span>
                ) : (
                    <Badge key={key} variant={variant} className={className} aria-label={ariaLabel}>
                        {content}
                    </Badge>
                ),
            )}
        </div>
    );
}
